import { inject } from '@angular/core';
import { Router, type CanActivateFn } from '@angular/router';
import { toObservable } from '@angular/core/rxjs-interop';
import { filter, map, take } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { BillingService } from '../services/billing.service';

function evaluate(
  auth: AuthService,
  billing: BillingService,
  router: Router
): boolean | ReturnType<typeof router.createUrlTree> {
  const user = auth.currentUser();

  if (!user) {
    return router.createUrlTree(['/auth']);
  }

  if (auth.requiresEmailVerification()) {
    return router.createUrlTree(['/auth/verify-email'], { queryParams: { expired: 'true' } });
  }

  if (!auth.hasRole()) {
    return router.createUrlTree(['/onboarding']);
  }

  if (!billing.hasActiveAccess()) {
    return router.createUrlTree(['/billing']);
  }

  return true;
}

export const authGuard: CanActivateFn = () => {
  const auth = inject(AuthService);
  const billing = inject(BillingService);
  const router = inject(Router);

  // Short-circuit synchronously on subsequent navigations once the app has loaded.
  // On hard refresh, both Auth state and Firestore profile need to settle first —
  // without this wait a returning user with a role briefly hits /auth.
  if (auth.ready()) {
    return evaluate(auth, billing, router);
  }

  return toObservable(auth.ready).pipe(
    filter((ready) => ready),
    take(1),
    map(() => evaluate(auth, billing, router))
  );
};
